import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { LogIn, LogOut, Mail, Trash2, Eye, Calendar, Upload, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import ContentEditor from "@/components/ContentEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      // Store token for Authorization header fallback
      if (data.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      }
      onLogin(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        className="w-full max-w-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 data-testid="admin-login-title" className="font-['Black_Han_Sans'] text-4xl text-[#F4EFE6] mb-2 uppercase">Admin</h1>
        <p className="font-mono-label text-xs text-[#F4EFE6]/40 mb-8">Sign in to manage submissions</p>

        <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-5">
          <Input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] h-12"
          />
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] h-12"
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="flex items-center gap-2 bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-lg uppercase tracking-wider px-8 py-2.5 hover:bg-[#FF5500]/80 transition-all duration-300 disabled:opacity-50"
          >
            <LogIn size={16} />
            {loading ? "SIGNING IN..." : "SIGN IN"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function SubmissionsDashboard({ user, onLogout }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("content");

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/submissions`, { withCredentials: true });
        setSubmissions(data);
      } catch (err) {
        toast.error("Failed to load submissions.");
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, []);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/submissions/${id}/read`, {}, { withCredentials: true });
      setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, read: true } : s)));
    } catch (err) {
      toast.error("Failed to update.");
    }
  };

  const deleteSubmission = async (id) => {
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, { withCredentials: true });
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error("Failed to delete.");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout error:", err);
    }
    onLogout();
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 data-testid="admin-dashboard-title" className="font-['Black_Han_Sans'] text-4xl md:text-5xl text-[#F4EFE6]">Admin Panel</h1>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#F4EFE6]/40 hover:text-[#FF5500] transition-colors text-sm"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {["content", "media", "submissions", "settings"].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`font-mono-label text-xs px-4 py-2 border uppercase transition-all ${
                activeTab === tab ? "bg-[#FF5500] border-[#FF5500] text-[#F4EFE6]" : "border-[#FF5500]/20 text-[#F4EFE6]/60 hover:border-[#FF5500]/50"
              }`}
              data-testid={`admin-tab-${tab}`}>
              {tab}
            </button>
          ))}
        </div>

        {/* Content Editor */}
        {activeTab === "content" && <ContentEditor />}

        {/* Media */}
        {activeTab === "media" && (
          <>
            <VideoUpload />
            <div className="mt-6"><MediaUpload /></div>
          </>
        )}

        {/* Settings */}
        {activeTab === "settings" && <PasswordChange />}

        {/* Submissions */}
        {activeTab === "submissions" && (
        <div>
          <h2 className="font-['Black_Han_Sans'] text-3xl text-[#F4EFE6] mb-2">Submissions</h2>
          <p className="text-sm text-[#F4EFE6]/40 mb-6">
            {submissions.filter((s) => !s.read).length} unread of {submissions.length} total
          </p>

        {loading ? (
          <p className="text-[#F4EFE6]/40">Loading...</p>
        ) : submissions.length === 0 ? (
          <p className="text-[#F4EFE6]/40">No submissions yet.</p>
        ) : (
          <div className="space-y-4">
            {submissions.map((sub) => (
              <motion.div
                key={sub.id}
                data-testid={`submission-${sub.id}`}
                className={`border ${sub.read ? "border-[#FF5500]/20]/40" : "border-[#FF5500]/40"} bg-[#0c1128] p-5 md:p-6 transition-all`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-['Black_Han_Sans'] text-xl text-[#F4EFE6]">{sub.name}</h3>
                      {!sub.read && <span className="text-[10px] uppercase tracking-wider bg-[#FF5500] text-[#F4EFE6] px-2 py-0.5">New</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#F4EFE6]/40 mb-3">
                      <span className="flex items-center gap-1"><Mail size={12} /> {sub.email}</span>
                      {sub.venue && <span>Venue: {sub.venue}</span>}
                      {sub.event_date && <span className="flex items-center gap-1"><Calendar size={12} /> {sub.event_date}</span>}
                    </div>
                    <p className="text-[#F4EFE6]/70 text-sm leading-relaxed">{sub.message}</p>
                    <p className="text-xs text-[#F4EFE6]/40/50 mt-3">{sub.created_at}</p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {!sub.read && (
                      <button
                        data-testid={`mark-read-${sub.id}`}
                        onClick={() => markRead(sub.id)}
                        className="p-2 text-[#F4EFE6]/40 hover:text-[#F4EFE6] transition-colors"
                        title="Mark as read"
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    <button
                      data-testid={`delete-${sub.id}`}
                      onClick={() => deleteSubmission(sub.id)}
                      className="p-2 text-[#F4EFE6]/40 hover:text-[#FF5500] transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        </div>
        )}
      </div>
    </div>
  );
}

function VideoUpload() {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [currentVideo, setCurrentVideo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-config`, { withCredentials: true })
      .then(({ data }) => { if (data.path) setCurrentVideo(data.path); })
      .catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please select a video file.");
      return;
    }
    setUploading(true);
    setProgress("Uploading...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Save as hero video config
      await axios.post(`${API}/admin/set-hero-video`, { path: data.path }, { withCredentials: true });
      setCurrentVideo(data.path);
      setProgress("");
      toast.success("Video uploaded! Refresh the homepage to see it.");
    } catch (err) {
      toast.error("Upload failed. Try again.");
      setProgress("");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-[#FF5500]/20 bg-[#0c1128] p-6">
      <h2 className="font-['Black_Han_Sans'] text-2xl text-[#F4EFE6] mb-4 uppercase">Hero Video</h2>
      <p className="text-sm text-[#F4EFE6]/40 mb-4">Upload an .mp4 video for the homepage hero background.</p>
      {currentVideo && (
        <p className="text-xs text-[#1DB954] mb-4">Current video: {currentVideo}</p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        onChange={handleUpload}
        className="hidden"
        data-testid="video-upload-input"
      />
      <button
        data-testid="video-upload-button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-lg uppercase tracking-wider px-6 py-2.5 hover:bg-[#FF5500]/80 transition-all duration-300 disabled:opacity-50"
      >
        <Upload size={16} />
        {uploading ? progress || "UPLOADING..." : "UPLOAD VIDEO"}
      </button>
    </div>
  );
}

function MediaUpload() {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState("all");
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [copied, setCopied] = useState(null);
  const [uploadGroup, setUploadGroup] = useState("ungrouped");
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const loadData = () => {
      axios.get(`${API}/admin/media`, { withCredentials: true })
        .then(({ data }) => setFiles(data))
        .catch(() => {});
      axios.get(`${API}/admin/media-groups`, { withCredentials: true })
        .then(({ data }) => setGroups(data))
        .catch(() => {});
    };
    loadData();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("group", uploadGroup);
      const { data } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newFile = {
        id: data.file_id,
        filename: file.name,
        path: data.path,
        url: `${API_BASE}/files/${data.path}`,
        group: uploadGroup,
      };
      setFiles((prev) => [newFile, ...prev]);
      toast.success("Uploaded! URL copied to clipboard.");
      navigator.clipboard.writeText(newFile.url).catch(() => {});
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/admin/media-groups`, { name }, { withCredentials: true });
      setGroups((prev) => [...prev, { name }]);
      setNewGroupName("");
      setShowNewGroup(false);
      toast.success(`Group "${name}" created.`);
    } catch (err) {
      toast.error("Failed to create group.");
    }
  };

  const moveToGroup = async (fileId, group) => {
    try {
      await axios.patch(`${API}/admin/media/${fileId}/group`, { group }, { withCredentials: true });
      setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, group } : f));
    } catch (err) {
      toast.error("Failed to move file.");
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/admin/media/${fileId}`, { withCredentials: true });
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success("File deleted.");
    } catch (err) {
      toast.error("Failed to delete file.");
    }
  };

  const startRename = (file) => {
    setRenaming(file.id);
    setRenameValue(file.filename);
  };

  const saveRename = async (fileId) => {
    const name = renameValue.trim();
    if (!name) return;
    try {
      await axios.patch(`${API}/admin/media/${fileId}/rename`, { filename: name }, { withCredentials: true });
      setFiles((prev) => prev.map((f) => f.id === fileId ? { ...f, filename: name } : f));
      setRenaming(null);
      toast.success("Renamed.");
    } catch (err) {
      toast.error("Failed to rename.");
    }
  };

  const copyUrl = (url, idx) => {
    navigator.clipboard.writeText(url);
    setCopied(idx);
    toast.success("URL copied!");
    setTimeout(() => setCopied(null), 2000);
  };

  const filteredFiles = activeGroup === "all" ? files : files.filter((f) => (f.group || "ungrouped") === activeGroup);
  const allGroups = ["all", "ungrouped", ...groups.map((g) => g.name)];

  return (
    <div className="border border-[#FF5500]/20 bg-[#0c1128] p-6">
      <h2 className="font-['Black_Han_Sans'] text-2xl text-[#F4EFE6] mb-2 uppercase">Media Library</h2>
      <p className="text-sm text-[#F4EFE6]/40 mb-4">Upload photos or files. Organize into groups. Click URL to copy.</p>

      {/* Group Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {allGroups.map((g) => (
          <button
            key={g}
            onClick={() => setActiveGroup(g)}
            className={`font-mono-label text-[10px] px-3 py-1.5 border transition-all uppercase ${
              activeGroup === g
                ? "bg-[#FF5500] border-[#FF5500] text-[#F4EFE6]"
                : "border-[#FF5500]/20 text-[#F4EFE6]/40 hover:text-[#F4EFE6] hover:border-[#FF5500]/50"
            }`}
            data-testid={`group-tab-${g}`}
          >
            {g} ({g === "all" ? files.length : files.filter((f) => (f.group || "ungrouped") === g).length})
          </button>
        ))}
        {!showNewGroup ? (
          <button
            onClick={() => setShowNewGroup(true)}
            className="font-mono-label text-[10px] px-3 py-1.5 border border-dashed border-[#FF5500]/30 text-[#FF5500]/50 hover:text-[#FF5500] hover:border-[#FF5500] transition-all"
            data-testid="new-group-button"
          >
            + NEW GROUP
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createGroup()}
              placeholder="Group name"
              className="bg-transparent border-b border-[#FF5500]/30 text-[#F4EFE6] text-sm px-1 py-1 w-32 focus:outline-none focus:border-[#FF5500]"
              autoFocus
              data-testid="new-group-input"
            />
            <button onClick={createGroup} className="font-mono-label text-[10px] text-[#FF5500] hover:text-[#F4EFE6]">SAVE</button>
            <button onClick={() => { setShowNewGroup(false); setNewGroupName(""); }} className="font-mono-label text-[10px] text-[#F4EFE6]/30 hover:text-[#F4EFE6]">CANCEL</button>
          </div>
        )}
      </div>

      {/* Upload */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={uploadGroup}
          onChange={(e) => setUploadGroup(e.target.value)}
          className="bg-[#080810] border border-[#FF5500]/20 text-[#F4EFE6] text-sm px-3 py-2 font-mono-label focus:outline-none focus:border-[#FF5500]"
          data-testid="upload-group-select"
        >
          <option value="ungrouped">Ungrouped</option>
          {groups.map((g) => (
            <option key={g.name} value={g.name}>{g.name}</option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleUpload}
          className="hidden"
          data-testid="media-upload-input"
        />
        <button
          data-testid="media-upload-button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-lg uppercase tracking-wider px-6 py-2 hover:bg-[#FF5500]/80 transition-all duration-300 disabled:opacity-50"
        >
          <Upload size={16} />
          {uploading ? "UPLOADING..." : "UPLOAD"}
        </button>
      </div>

      {/* File List */}
      {filteredFiles.length > 0 ? (
        <div className="space-y-2">
          {filteredFiles.map((f, i) => (
            <div key={f.id || i} className="flex items-center gap-3 py-3 border-b border-[#FF5500]/10">
              {/* Thumbnail */}
              {f.url && (f.filename || "").match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                <img src={f.url} alt="" className="w-10 h-10 object-cover shrink-0 border border-[#FF5500]/10" />
              )}
              <div className="flex-1 min-w-0">
                {renaming === f.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveRename(f.id)}
                      className="bg-transparent border-b border-[#FF5500]/30 text-[#F4EFE6] text-sm px-0 py-0.5 flex-1 focus:outline-none focus:border-[#FF5500]"
                      autoFocus
                    />
                    <button onClick={() => saveRename(f.id)} className="text-[#1DB954] hover:text-[#F4EFE6] transition-colors" title="Save"><Check size={14} /></button>
                    <button onClick={() => setRenaming(null)} className="text-[#F4EFE6]/30 hover:text-[#F4EFE6] transition-colors" title="Cancel"><X size={14} /></button>
                  </div>
                ) : (
                  <p className="text-sm text-[#F4EFE6] font-bold truncate">{f.filename}</p>
                )}
                <div className="flex items-center gap-2">
                  <p className="font-mono-label text-[10px] text-[#F4EFE6]/20 truncate">{f.url}</p>
                  {f.group && f.group !== "ungrouped" && (
                    <span className="font-mono-label text-[8px] border border-[#FF5500]/20 text-[#FF5500]/60 px-1.5 py-0.5 uppercase shrink-0">{f.group}</span>
                  )}
                </div>
              </div>
              {/* Move to group */}
              {groups.length > 0 && (
                <select
                  value={f.group || "ungrouped"}
                  onChange={(e) => moveToGroup(f.id, e.target.value)}
                  className="bg-[#080810] border border-[#FF5500]/10 text-[#F4EFE6]/40 text-[10px] px-2 py-1 font-mono-label shrink-0 focus:outline-none"
                  data-testid={`move-group-${f.id}`}
                >
                  <option value="ungrouped">Ungrouped</option>
                  {groups.map((g) => (
                    <option key={g.name} value={g.name}>{g.name}</option>
                  ))}
                </select>
              )}
              <button
                onClick={() => startRename(f)}
                className="shrink-0 text-[#F4EFE6]/30 hover:text-[#FF5500] transition-colors p-1"
                title="Rename"
                data-testid={`rename-${f.id}`}
              >
                <Pencil size={13} />
              </button>
              <button
                onClick={() => deleteFile(f.id)}
                className="shrink-0 text-[#F4EFE6]/30 hover:text-red-500 transition-colors p-1"
                title="Delete"
                data-testid={`delete-media-${f.id}`}
              >
                <Trash2 size={13} />
              </button>
              <button
                onClick={() => copyUrl(f.url, i)}
                className={`shrink-0 font-mono-label text-[10px] px-3 py-1 border transition-all ${
                  copied === i
                    ? "border-[#1DB954] text-[#1DB954]"
                    : "border-[#FF5500]/30 text-[#FF5500] hover:bg-[#FF5500] hover:text-[#F4EFE6]"
                }`}
                data-testid={`copy-url-${i}`}
              >
                {copied === i ? "COPIED" : "COPY URL"}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-[#F4EFE6]/30">{activeGroup === "all" ? "No files uploaded yet." : "No files in this group."}</p>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-[#F4EFE6]/40">Loading...</div>;
  }

  return user ? (
    <SubmissionsDashboard user={user} onLogout={() => setUser(null)} />
  ) : (
    <LoginForm onLogin={setUser} />
  );
}

function PasswordChange() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { withCredentials: true });
      toast.success("Password updated!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-md">
      <h2 className="font-['Black_Han_Sans'] text-3xl text-[#F4EFE6] mb-6 uppercase">Change Password</h2>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="font-mono-label text-xs text-[#FF5500] mb-1 block">Current Password</label>
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            data-testid="current-password-input"
            className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] h-12"
          />
        </div>
        <div>
          <label className="font-mono-label text-xs text-[#FF5500] mb-1 block">New Password</label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            data-testid="new-password-input"
            placeholder="Min 8 characters"
            className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] h-12"
          />
        </div>
        <div>
          <label className="font-mono-label text-xs text-[#FF5500] mb-1 block">Confirm New Password</label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            data-testid="confirm-password-input"
            className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] h-12"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="change-password-button"
          className="flex items-center gap-2 bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-lg uppercase tracking-wider px-8 py-2.5 hover:bg-[#FF5500]/80 transition-all duration-300 disabled:opacity-50"
        >
          {saving ? "UPDATING..." : "UPDATE PASSWORD"}
        </button>
      </form>
    </div>
  );
}
